import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import { getCampStatus } from '../../utils/campLogic';

const DAYS = [
  { day: 1, title: 'The Christian', subtitle: 'Mindset & Calling' },
  { day: 2, title: 'The Creator', subtitle: 'Writing' },
  { day: 3, title: 'The Caretaker', subtitle: 'Publishing' },
  { day: 4, title: 'The Connector', subtitle: 'Marketing' },
  { day: 5, title: 'Compass Summary', subtitle: 'Review & Export' },
];

export default function MessHall() {
  const { user } = useAuth();
  const { currentEnrollment } = useAcademy();
  const [contents, setContents] = useState({});
  const [assemblyMap, setAssemblyMap] = useState({});
  const [settings, setSettings] = useState({});
  const [activeDay, setActiveDay] = useState(1);
  const [loading, setLoading] = useState(true);
  const [adminBypass, setAdminBypass] = useState(true);
  const location = useLocation();

  const runPassType = currentEnrollment?.pass_type ?? user?.pass_type;
  const runUser = { ...user, pass_type: runPassType };
  const { isAAP, currentTrailDay, windowOpen } = getCampStatus(runUser, settings);
  const isLockedOut = !windowOpen && !(user?.role === 'admin' && adminBypass);
  const canWatchReplay = (user?.role === 'admin' && adminBypass) || isAAP;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resContent, resAssembly, resSettings] = await Promise.all([
          api.get('academy_content_gwaacademy', { block: 'messhall' }),
          api.get('academy_content_gwaacademy', { block: 'assembly' }),
          api.get('settings_gwaacademy')
        ]);
        
        const settingsData = Array.isArray(resSettings) ? resSettings : resSettings.data || [];
        const sMap = {};
        settingsData.forEach(s => {
          if (s.setting_key) sMap[s.setting_key] = s.value;
        });
        setSettings(sMap);

        const data = Array.isArray(resContent) ? resContent : resContent.data || [];
        const map = {};
        data.filter(item => item.is_active == 1 || item.is_active === true).forEach(c => {
          map[c.day] = c;
        });
        setContents(map);

        const asmData = Array.isArray(resAssembly) ? resAssembly : resAssembly.data || [];
        const aMap = {};
        asmData.forEach(c => {
          aMap[c.day] = c;
        });
        setAssemblyMap(aMap);

        // Handle active day selection from URL
        const params = new URLSearchParams(location.search);
        const urlDay = parseInt(params.get('day'));
        
        if (urlDay && urlDay >= 1 && urlDay <= 5) {
          setActiveDay(urlDay);
        } else {
          setActiveDay(1);
        }
      } catch (err) {
        console.error("Failed to load mess hall content", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.search]);

  if (loading) {
    return (
      <div className="p-8 text-center text-muted flex flex-col items-center justify-center min-h-[400px]">
        <SafeIcon name="Loader" className="animate-spin text-4xl text-amber mb-4" />
        <p>Heading to the Midday Lab...</p>
      </div>
    );
  }

  if (isLockedOut) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 animate-fadeIn">
        <Card className="text-center py-16 px-8 border-2 border-forest-green shadow-xl bg-[#EAF1E6]">
          <div className="w-24 h-24 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-6 shadow-md text-white">
            <SafeIcon name="Users" className="text-5xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-4 font-poppins">Midday Lab Access Expired</h1>
          <p className="text-lg text-forest-green font-medium mb-6 max-w-2xl mx-auto">
            {settings.replay_expiry_msg || "Free pass replay access has expired. Upgrade to All-Access to continue watching all academy sessions."}
          </p>
          <a href="/#/upgrade">
            <button className="bg-amber text-white font-bold px-8 py-3 rounded hover:bg-orange-600 transition-colors">
              Upgrade to All-Access
            </button>
          </a>
        </Card>
      </div>
    );
  }

  const isEarlyLocked = currentTrailDay === 0 && user?.early_access != 1 && !(user?.role === 'admin' && adminBypass);

  if (isEarlyLocked) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 animate-fadeIn">
        <Card className="text-center py-16 px-8 border-2 border-tan shadow-sm">
          <div className="w-20 h-20 bg-amber/20 rounded-full flex items-center justify-center mx-auto mb-6 text-amber">
            <SafeIcon name="Clock" className="text-4xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-4 font-poppins">Academy Hasn't Started Yet</h1>
          <p className="text-lg text-muted mb-6 max-w-2xl mx-auto">
            Midday Lab sessions unlock at 12:30 PM EST each day of academy. Check back on Day 1!
          </p>
        </Card>
      </div>
    );
  }

  const content = contents[activeDay];
  const activeAsm = assemblyMap[activeDay];
  const now = new Date();
  const isContentTimeUnlocked = content?.unlock_time ? (now >= new Date(content.unlock_time)) : (currentTrailDay >= activeDay);
  const isUnlocked = (user?.role === 'admin' && adminBypass) || user?.early_access == 1 || isContentTimeUnlocked;
  const currentPersona = activeAsm?.persona_label || DAYS[activeDay - 1].title;
  const currentDayTitle = activeAsm?.day_title || content?.session_title || DAYS[activeDay - 1].subtitle;

  return (
    <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8 pb-20 animate-fadeIn">
      {/* Sidebar Navigation */}
      <div className="w-full lg:w-72 shrink-0 space-y-4 no-print">
        <div className="bg-[#EAF1E6] border-l-4 border-forest-green p-4 rounded-r-md mb-6">
          <h1 className="text-xl font-bold text-dark-green flex items-center gap-2">
            <SafeIcon name="Users" className="text-amber" /> Midday Lab
          </h1>
          <p className="text-xs mt-1 text-forest-green font-medium">Daily Check-in</p>
        </div>

        {/* Admin Unlock Bar */}
        {user?.role === 'admin' && (
          <div className="p-3 bg-dark-green text-white rounded-md flex items-center justify-between text-xs">
            <span className="font-bold flex items-center gap-1">
              <SafeIcon name={adminBypass ? 'Unlock' : 'Lock'} className="text-amber" /> Admin View
            </span>
            <button
              onClick={() => setAdminBypass(!adminBypass)}
              className="bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded text-[11px] font-bold"
            >
              {adminBypass ? 'Unlocked' : 'Simulate Lock'}
            </button>
          </div>
        )}
        
        <nav className="space-y-2">
          {DAYS.map((d) => {
            const dayContent = contents[d.day];
            const dayTimeUnlocked = dayContent?.unlock_time ? (now >= new Date(dayContent.unlock_time)) : (currentTrailDay >= d.day);
            const unlocked = (user?.role === 'admin' && adminBypass) || user?.early_access == 1 || dayTimeUnlocked;
            const active = activeDay === d.day;
            const asm = assemblyMap[d.day];
            const personaLabel = asm?.persona_label || d.title;
            const dayTitle = asm?.day_title || contents[d.day]?.session_title || d.subtitle;

            return (
              <button
                key={d.day}
                onClick={() => setActiveDay(d.day)}
                className={`w-full text-left p-4 rounded-md border transition-all flex items-center justify-between
                  ${active ? 'bg-forest-green border-forest-green text-white shadow-md' : 
                    'bg-white border-tan hover:border-amber text-dark-green'}`}
              >
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider mb-1 opacity-80">Day {d.day}</div>
                  <div className={`font-bold ${active ? 'text-white' : 'text-dark-green'}`}>{personaLabel}</div>
                  <div className={`text-xs ${active ? 'text-tan' : 'text-muted'}`}>{dayTitle}</div>
                </div>
                <div>
                  {!unlocked ? (
                    <SafeIcon name="Lock" className="text-gray-400" />
                  ) : active ? (
                    <SafeIcon name="ChevronRight" />
                  ) : (
                    <SafeIcon name="CheckCircle" className="text-amber" />
                  )}
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center bg-white border-b border-tan pb-4 mb-6 pt-2 no-print">
          <div>
            <h2 className="text-2xl font-bold text-dark-green">Day {activeDay}: {currentPersona}</h2>
            <p className="text-muted">{currentDayTitle}</p>
          </div>
        </div>

        {!isAAP && (
          <div className="bg-[#fdfaf6] border border-tan rounded-lg p-5 flex items-start gap-4 mb-8">
            <SafeIcon name="Info" className="text-amber mt-1 shrink-0" />
            <div className="text-sm text-dark-green leading-relaxed">
              <span className="font-bold">Replay Access:</span> Your paid pass includes the Midday Lab description, meeting link, and resources. Upgrade to All-Access for video replay access.
            </div>
          </div>
        )}

        {content && isUnlocked ? (
          <div className="space-y-8">
            <Card className="overflow-hidden border-2 border-forest-green shadow-md">
              <div className="p-6">
                <div className="mb-2 inline-flex items-center gap-2 text-xs font-bold text-forest-green uppercase tracking-widest bg-forest-green/10 px-3 py-1 rounded-full w-fit">
                  Day {activeDay}
                </div>
                <h2 className="text-2xl font-bold text-dark-green mb-6 font-poppins leading-tight">
                  {content.session_title || `Midday Lab Day ${activeDay}`}
                </h2>
                
                {/* Video Area */}
                {canWatchReplay ? (
                  <div className="aspect-video bg-black rounded-md overflow-hidden relative shadow-inner group mb-6">
                    {content.video_embed ? (
                      <div 
                        className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full border-0 absolute inset-0" 
                        dangerouslySetInnerHTML={{ __html: content.video_embed }} 
                      />
                    ) : content.video_url ? (
                      <iframe 
                        src={content.video_url} 
                        className="w-full h-full border-0 absolute inset-0"
                        allow="autoplay; fullscreen; picture-in-picture" 
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-500 flex-col gap-2">
                        <SafeIcon name="VideoOff" className="text-3xl" />
                        <span className="text-sm font-medium">Video currently unavailable</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-6 rounded-md border-2 border-dashed border-tan bg-cream px-6 py-8 text-center">
                    <SafeIcon name="Lock" className="mx-auto mb-3 text-3xl text-amber" />
                    <h3 className="text-lg font-bold text-dark-green">
                      Midday Lab Replay is an All-Access Benefit
                    </h3>
                    <p className="mx-auto mt-2 max-w-xl text-sm text-muted">
                      Your paid pass includes this session's description, meeting details, and resources below. Upgrade to All-Access to unlock its video replay.
                    </p>
                  </div>
                )}

                {/* Description text */}
                {content.session_description && (
                  <div className="text-gray-700 leading-relaxed mb-6 whitespace-pre-wrap">
                    {content.session_description}
                  </div>
                )}

                {content.slides_url && (
                  <div className="mt-6 pt-4 border-t border-tan">
                    <a href={content.slides_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gwa-blue font-bold hover:text-blue-800 transition-colors">
                      <SafeIcon name="Download" /> Download Session Slides
                    </a>
                  </div>
                )}
              </div>
            </Card>
          </div>
        ) : (
          <Card className="text-center py-16 bg-gray-50 border-dashed border-2 animate-fadeIn relative overflow-hidden">
            <div className="w-20 h-20 bg-[#EDE0CC] rounded-full flex items-center justify-center mb-6 mx-auto mt-4">
              <SafeIcon name="Lock" className="w-9 h-9 text-[#2D5016]" />
            </div>
            <h3 className="text-2xl font-bold text-dark-green mb-2">Session Not Yet Open</h3>
            <p className="text-muted max-w-sm mx-auto leading-relaxed">
              This session unlocks at <strong>12:30 PM EST</strong> on Day {activeDay}.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}